import { useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";

interface ChangeFundingSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newFundingSourceName?: string;
  onConfirm: () => Promise<void>;
}

export const ChangeFundingSourceDialog = ({
  open,
  onOpenChange,
  newFundingSourceName,
  onConfirm,
}: ChangeFundingSourceDialogProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setError(null);
    }
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (e) {
      setError(
        isApiError(e) ? getApiErrorMessage(e) : "Unable to update funding source. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Change Funding Source</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="flex flex-col gap-2">
              <span>
                Change this workbench&rsquo;s funding source
                {newFundingSourceName ? (
                  <>
                    {" "}
                    to{" "}
                    <strong className="font-medium text-foreground">{newFundingSourceName}</strong>
                  </>
                ) : null}
                ?
              </span>
              <span>
                Collaborators without access to the selected funding source will not be able to
                launch new compute environments or workflow runs in this workbench and will only
                have read access to this workbench&rsquo;s files.
              </span>
              {error && <span className="text-destructive">{error}</span>}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
          <Button onClick={handleConfirm} disabled={submitting}>
            {submitting ? "Changing…" : "Change Funding Source"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
